import { RpcError } from '@protobuf-ts/runtime-rpc'
import { CropArea } from 'lib/crop-image'
import { CreateReturn, Status } from 'lib/rpc'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

export type UploadState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'error'; error: RpcError }
    | { status: 'ok'; file: File }

export interface ImageProcessorReturn {
    upload: UploadState
    crop: { x: number; y: number }
    zoom: number
    croppedArea: CropArea | null
    imageUrl: string | null
    uploadedFile: File | null
    handleFileDrop: (files: File[]) => void
    onCropComplete: (_: unknown, croppedAreaPixels: CropArea) => void
    setCrop: (crop: { x: number; y: number }) => void
    setZoom: (zoom: number) => void
    reset: () => void
}

export interface UseImageProcessorOptions {
    maxBytes?: number
    validTypes?: string[]
    errorMessageIds?: {
        fileSize?: string
        fileType?: string
    }
}

const DEFAULT_MAX_BYTES = 1 * 1024 * 1024 // 1MB
const DEFAULT_VALID_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const DEFAULT_ERROR_IDS = {
    fileSize: 'image_upload_file_size_error',
    fileType: 'image_upload_file_type_error',
}

export const uploadToCreateReturn = (upload: UploadState): CreateReturn<unknown> => {
    switch (upload.status) {
        case 'idle':
            return { status: Status.INIT }
        case 'loading':
            return { status: Status.LOADING }
        case 'ok':
            return { status: Status.OK, item: upload.file }
        case 'error':
            return { status: Status.ERROR, error: upload.error }
        default:
            return { status: Status.INIT }
    }
}

/**
 * Custom hook for image processing, validation, and cropping
 *
 * Manages the complete lifecycle of image upload including:
 * - File validation (size and type)
 * - Image preview generation
 * - Crop area management
 * - Zoom controls
 *
 * @param options - Configuration options for max size, valid types, and error messages
 * @returns Image processor state and control functions
 *
 * @example
 * ```tsx
 * const imageProcessor = useImageProcessor({
 *   maxBytes: 2 * 1024 * 1024, // 2MB
 *   validTypes: ['image/png'],
 *   errorMessageIds: {
 *     fileSize: 'custom_size_error',
 *     fileType: 'custom_type_error'
 *   }
 * })
 * ```
 */
export const useImageProcessor = (options?: UseImageProcessorOptions): ImageProcessorReturn => {
    const intl = useIntl()
    const maxBytes = options?.maxBytes ?? DEFAULT_MAX_BYTES
    const validTypes = options?.validTypes ?? DEFAULT_VALID_TYPES
    const errorIds = useMemo(() => ({ ...DEFAULT_ERROR_IDS, ...options?.errorMessageIds }), [options?.errorMessageIds])

    const [upload, setUpload] = useState<UploadState>({ status: 'idle' })
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [croppedArea, setCroppedArea] = useState<CropArea | null>(null)
    const [imageUrl, setImageUrl] = useState<string | null>(null)

    const validateImageFile = useCallback((file: File): string | null => {
        if (file.size > maxBytes) {
            return intl.formatMessage(
                {
                    id: errorIds.fileSize,
                    defaultMessage: 'File size must be less than {max_size} MB',
                },
                { max_size: Math.round(maxBytes / (1024 * 1024)) },
            )
        }
        if (!validTypes.includes(file.type)) {
            return intl.formatMessage({
                id: errorIds.fileType,
                defaultMessage: 'Only PNG, JPEG, and WebP files are supported',
            })
        }
        return null
    }, [intl, maxBytes, validTypes, errorIds])

    const handleFileDrop = useCallback((files: File[]): void => {
        if (files.length === 0) return

        const file = files[0]
        const validationError = validateImageFile(file)

        if (validationError) {
            setUpload({ status: 'error', error: new RpcError(validationError, 'INVALID_ARGUMENT') })
            return
        }

        setUpload({ status: 'ok', file })
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setCroppedArea(null)
    }, [validateImageFile])

    const onCropComplete = useCallback((_: unknown, croppedAreaPixels: CropArea): void => {
        setCroppedArea(croppedAreaPixels)
    }, [])

    const reset = useCallback((): void => {
        setUpload({ status: 'idle' })
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setCroppedArea(null)
        setImageUrl(null)
    }, [])

    // Create data URL when file changes
    const uploadedFile = upload.status === 'ok' ? upload.file : null

    useEffect(() => {
        if (!uploadedFile) {
            setImageUrl(null)
            return
        }

        const reader = new FileReader()
        reader.onload = (e): void => {
            const result = e.target?.result as string
            setImageUrl(result)
        }
        reader.readAsDataURL(uploadedFile)
    }, [uploadedFile])

    return {
        upload,
        crop,
        zoom,
        croppedArea,
        imageUrl,
        uploadedFile,
        handleFileDrop,
        onCropComplete,
        setCrop,
        setZoom,
        reset,
    }
}
